import React, { useEffect, useMemo, useRef, useState } from "react";

import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import API from "../../../api";
import { Button, SkeletonTableView, TableView } from "../../../components";
import { DataType } from "../../../GlobalConstants";


export const AttachRules = () => {
    const [campaignDataLoading, setCampaignDataLoading] = useState(false)
    const [rulesDataLoading, setRulesDataLoading] = useState(false)
    const [rulesData, setRulesData] = useState(null)
    const [attachRuleLoading, setAttachRuleLoading] = useState(false)
    const [campaignData, setCampaignData] = useState(null);
    const [searchParams] = useSearchParams();

    const configRef = useRef(null)
    const params = useParams()
    const navigate = useNavigate()

    const TableViewColumns = useMemo(() => [
        {
            name: "Rule Id",
            field: "id",
            type: DataType.TEXT
        },
        {
            name: "Name",
            field: "name",
            type: DataType.TEXT
        },
        {
            name: "Status",
            field: "status",
            type: DataType.TEXT
        },
        {
            name: "Start Date",
            field: "startDate",
            type: DataType.DATE
        },
        {
            name: "End Date",
            field: "endDate",
            type: DataType.DATE
        },
        {
            name: "Modified At",
            field: "updatedAt",
            type: DataType.DATE
        },
        {
            name: "Modified By",
            field: "updatedBy",
            type: DataType.TEXT
        },
        {
            children: (rowData) =>
                <div className="flex items-center gap-5">
                    {/* Should be checked if the rowData?.id is present in the rulesId of the campaign  */}
                    <div>{checkPriorityOrder(rowData?.id)}</div>
                </div>,
            name: "Priority",
            field: "priorityOrder",
            type: DataType.ACTION
        },
        {
            children: (rowData) =>
                <div className="flex items-center gap-5">
                    {/* Should be checked if the rowData?.id is present in the rulesId of the campaign  */}
                    <input type="checkbox" checked={checkIfRuleIdIsAttached(rowData?.id)} onChange={(evt) => {
                        evt?.stopPropagation()
                        toggleAttachRule(rowData?.id)
                    }} className="checkbox" />
                </div>,
            name: DataType.ACTION,
            type: DataType.ACTION
        }], [campaignData])

    const checkIfRuleIdIsAttached = (id) => {
        const { catalogInstanceDetails } = configRef.current || {}
        const { ruleId } = catalogInstanceDetails || {}
        if (ruleId && ruleId?.length) {
            return ruleId.includes(id)
        }
        return false
    }

    const checkPriorityOrder = (id) => {
        const { catalogInstanceDetails } = configRef.current || {}
        const { ruleId } = catalogInstanceDetails || {}
        if (ruleId && ruleId?.length) {
            const index = ruleId.indexOf(id)
            return index !== -1 ? index + 1 : "-"
        }
        return "-"
    }

    const toggleAttachRule = (id) => {
        const { catalogInstanceDetails: { ruleId } } = configRef.current
        if (ruleId && ruleId.length) {
            if (ruleId.includes(id)) {
                const newRuleIds = ruleId.filter(ruleId => ruleId !== id)
                configRef.current.catalogInstanceDetails.ruleId = newRuleIds
            } else {
                configRef.current.catalogInstanceDetails.ruleId?.push(id)
            }
        } else {
            configRef.current.catalogInstanceDetails.ruleId?.push(id)
        }

        setCampaignData({
            ...(configRef.current)
        })
    }


    const getCampaignData = async ({ campaignId }) => {
        try {
            setCampaignDataLoading(true)
            const response = await API.getCampaignById({
                campaignId
            })
            setCampaignData({
                ...response,
                catalogInstanceDetails: {
                    ...(response.catalogInstanceDetails),
                    ruleId: response.catalogInstanceDetails.ruleId ? response.catalogInstanceDetails.ruleId : []
                }
            })
        } catch (err) {
            console.log("getCampaignData by id:", err)
            setCampaignDataLoading(false)
        }
    }

    const getRulesData = async ({
        offset = 0,
        status = "PUBLISHED",
        type,
        pageSize = 50, }) => {
        try {
            setRulesDataLoading(true)
            const { items, currentPage, totalCount } = await API.getAllRules({ offset, status, type, pageSize })
            setRulesDataLoading(false)

            setRulesData({
                pagination: {
                    offset: currentPage,
                    total: totalCount,
                    pageSize: 50
                },
                data: items
            })

        } catch (err) {
            console.log("getRulesData by id:", err)
            setRulesDataLoading(false)
        }
    }

    const attachRulesToCampaign = async () => {
        //check if rules are attached or not
        if (!configRef.current?.catalogInstanceDetails?.ruleId?.length) {
            toast.error("Please select at least one rule to attach")
            return
        }
        try {
            setAttachRuleLoading(true)
            const dataJson = {
                ...configRef.current
            }
            delete dataJson?.catalogInstanceDetails?.status

            const response = await API.updateCampaign({ dataJson })
            setAttachRuleLoading(false)
            toast.success("Rules attached successfully")
            if (response) {
                const { catalogInstanceDetails } = response
                if (catalogInstanceDetails?.status === "SUCCESS") {
                    navigate(`/campaigns/campaign/${params.campaignId}?mode=view`)
                }
            }
        } catch (err) {
            console.log("attachRulesToCampaign err: ", err)
            setAttachRuleLoading(false)
            toast.error("Unable to attach rules")
        }
    }

    useEffect(() => {
        const { campaignId } = params
        getCampaignData({ campaignId })
        getRulesData({ type: searchParams?.get('type') })
    }, [])

    useEffect(() => {
        if (campaignData) {
            configRef.current = campaignData //in this way whenever I am changing the state I can manual trigger config to update and anyways render function call is happening
            setCampaignDataLoading(false)
        }
    }, [campaignData])

    return (
        <div className="w-full h-full overflow-auto px-8 py-3">
            <p className="text-xl font-medium sticky -top-3 z-10 p-3 bg-white">Attach Rules to Campaign: {params?.campaignId}</p>
            {
                campaignDataLoading || rulesDataLoading ?
                    <SkeletonTableView columCount={9} rowCount={10} />
                    : null
            }
            {
                !rulesDataLoading && rulesData &&
                <TableView
                    data={rulesData?.data}
                    pagination={rulesData?.pagination}
                    columns={TableViewColumns}
                    onPageChange={() => {

                    }}
                />
            }
            <div className="flex justify-end items-center gap-2 mt-2">
                <Button
                    variant="primary"
                    disabled={attachRuleLoading || !configRef.current?.catalogInstanceDetails?.ruleId?.length}
                    onClick={attachRulesToCampaign}
                >
                    Attach Selected Rules
                </Button>
            </div>
        </div>
    )
}